import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerApi, magicQuoteApi } from '../../api/endpoints.ts';
import { Alert, Button, TextInput } from '../ui.tsx';
import SearchSelect from '../search-select.tsx';
import type { MagicQuoteCustomerDraft } from '@shared/magic-quote.ts';

/**
 * Who the walkthrough was for, decided after the walk instead of before it.
 *
 * A cleaner is allowed to start with nobody on file — knocking on a door is a
 * normal way to get work — so this is where that walkthrough gets its
 * customer. Two ways, side by side, because both are ordinary: the person is
 * already in the system, or they are not.
 *
 * The form arrives prefilled from what the walkthrough itself heard: a name
 * somebody said, a number they read out, the address on the mailbox. Every
 * field stays editable and nothing is written until the button is pressed —
 * the model proposes a customer record, a person creates one.
 */
export default function MagicQuoteCustomerCard({
  sessionId,
  draft,
  canCreate,
  onAttached,
}: {
  sessionId: string;
  /** What synthesis proposed, or null if it found nobody (or has not run). */
  draft: MagicQuoteCustomerDraft | null;
  /** Whether this user may write to the customer list. */
  canCreate: boolean;
  onAttached: () => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [existingId, setExistingId] = useState('');

  const fieldsFromDraft = (source: MagicQuoteCustomerDraft | null) => ({
    firstName: source?.firstName ?? '',
    lastName: source?.lastName ?? '',
    companyName: source?.companyName ?? '',
    email: source?.email ?? '',
    phone: source?.phone ?? '',
    billingAddressLine1: source?.addressLine1 ?? '',
    billingCity: source?.city ?? '',
    billingState: source?.state ?? '',
    billingPostalCode: source?.postalCode ?? '',
  });

  const [fields, setFields] = useState(() => fieldsFromDraft(draft));
  // Which fields the estimator has typed in. Everything else follows the
  // draft: this card stays mounted while the review page drains the capture
  // queue and re-organizes, so a suggestion that arrives (or improves) after
  // the first render has to reach the inputs. Only untouched ones — a form
  // that overwrites what somebody is typing is worse than a stale one.
  const touched = useRef(new Set<string>());
  useEffect(() => {
    const next = fieldsFromDraft(draft);
    setFields(
      (prior) =>
        Object.fromEntries(
          Object.entries(next).map(([key, value]) => [
            key,
            touched.current.has(key) ? prior[key as keyof typeof prior] : value,
          ]),
        ) as typeof prior,
    );
    // Keyed on the draft's contents rather than its identity: the query
    // refetches on a poll and hands back an equal-but-new object every time,
    // which as a dependency would re-run this on every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(draft)]);

  const customers = useQuery({
    queryKey: ['customers', 'magic-quote-attach', search],
    queryFn: () => customerApi.list({ q: search, pageSize: 20 }),
  });

  const attach = useMutation({
    mutationFn: (body: Parameters<typeof magicQuoteApi.setCustomer>[1]) => magicQuoteApi.setCustomer(sessionId, body),
    onSuccess: () => {
      setError(null);
      onAttached();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const set = (key: keyof typeof fields) => (value: string) => {
    touched.current.add(key);
    setFields((prior) => ({ ...prior, [key]: value }));
  };
  // The same rule the customer form applies: a record filed under no name at
  // all can never be found again.
  const named = Boolean(fields.firstName.trim() || fields.lastName.trim() || fields.companyName.trim());

  return (
    // Ringed rather than plain: this is the one card on the review screen that
    // is asking for something, and it has to read that way beside four that
    // are merely reporting.
    <section className="card mb-6 border-[color:var(--color-brand)]/40 ring-1 ring-[color:var(--color-brand)]/15">
      <h2 className="text-lg font-semibold">{t('magicQuote.customer.title')}</h2>
      <p className="mt-1 text-sm text-[color:var(--color-muted)]">{t('magicQuote.customer.body')}</p>

      {error ? (
        <div className="mt-3">
          <Alert tone="error" title={t('magicQuote.customer.errorTitle')}>
            {error}
          </Alert>
        </div>
      ) : null}

      {draft ? (
        // The fields below are already filled in with this; saying where they
        // came from is what makes them safe to trust or safe to overwrite.
        <p className="mt-3 rounded-lg bg-[color:var(--color-brand-soft)] px-3 py-2 text-sm text-[color:var(--color-brand-strong)]">
          {t('magicQuote.customer.suggested', { percent: Math.round(draft.confidence * 100) })}
        </p>
      ) : (
        <p className="mt-3 rounded-lg bg-[color:var(--color-surface)] px-3 py-2 text-sm text-[color:var(--color-muted)]">
          {t('magicQuote.customer.noSuggestion')}
        </p>
      )}

      {canCreate ? (
        <div className="mt-3 flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <TextInput
              label={t('customerForm.firstName')}
              value={fields.firstName}
              onChange={(event) => set('firstName')(event.target.value)}
            />
            <TextInput
              label={t('customerForm.lastName')}
              value={fields.lastName}
              onChange={(event) => set('lastName')(event.target.value)}
            />
            <TextInput
              label={t('customerForm.companyName')}
              value={fields.companyName}
              onChange={(event) => set('companyName')(event.target.value)}
            />
            <TextInput
              label={t('customerForm.phone')}
              value={fields.phone}
              onChange={(event) => set('phone')(event.target.value)}
            />
            <TextInput
              label={t('customerForm.email')}
              type="email"
              value={fields.email}
              onChange={(event) => set('email')(event.target.value)}
            />
            <TextInput
              label={t('customerForm.addressLine1')}
              value={fields.billingAddressLine1}
              onChange={(event) => set('billingAddressLine1')(event.target.value)}
            />
            <TextInput
              label={t('customerForm.city')}
              value={fields.billingCity}
              onChange={(event) => set('billingCity')(event.target.value)}
            />
            <TextInput
              label={t('customerForm.state')}
              value={fields.billingState}
              onChange={(event) => set('billingState')(event.target.value)}
            />
            <TextInput
              label={t('customerForm.postalCode')}
              value={fields.billingPostalCode}
              onChange={(event) => set('billingPostalCode')(event.target.value)}
            />
          </div>

          <div>
            <Button
              variant="primary"
              disabled={!named}
              busy={attach.isPending}
              onClick={() =>
                attach.mutate({
                  customer: Object.fromEntries(
                    // Blank fields are absent, not empty strings: a customer
                    // record with `email: ""` is a record that looks like it
                    // has an email everywhere it is read.
                    Object.entries(fields).map(([key, value]) => [key, value.trim() || null]),
                  ),
                })
              }
            >
              {t('magicQuote.customer.create')}
            </Button>
            {!named ? (
              <p className="mt-1 text-sm text-[color:var(--color-muted)]">{t('magicQuote.customer.needName')}</p>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="mt-3 text-sm text-[color:var(--color-muted)]">{t('magicQuote.customer.cannotCreate')}</p>
      )}

      <div className="mt-5 border-t border-[color:var(--color-line)] pt-4">
        <h3 className="text-sm font-semibold">{t('magicQuote.customer.existingTitle')}</h3>
        <div className="mt-2 flex flex-col gap-3">
          <SearchSelect
            label={t('magicQuote.home.customerLabel')}
            hint={t('searchSelect.hint')}
            placeholder={t('magicQuote.home.customerPlaceholder')}
            value={existingId}
            onChange={setExistingId}
            onSearch={setSearch}
            loading={customers.isFetching}
            errored={customers.isError}
            options={(customers.data?.items ?? []).map((customer) => ({
              value: customer.id,
              label: customer.displayName,
            }))}
          />
          <div>
            <Button
              disabled={!existingId}
              busy={attach.isPending}
              onClick={() => attach.mutate({ customerId: existingId })}
            >
              {t('magicQuote.customer.useExisting')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
